# Position routing

import { Alert, Text } from 'braid-design-system';
import { Directional } from '../../../../components/Directional/Directional';

<Alert tone="caution">
  <Text>
    Incorrect `seekSubOrgReference` values may lead to positions being posted to
    the wrong SEEK account or failing to post altogether.
  </Text>
</Alert>

An organization may structure their hiring differently based on internal business units, subsidiaries, or geographic locations.
Your software can supply a `seekSubOrgReference` to allow Ad Sync to route each position to the appropriate SEEK account.

The `seekSubOrgReference` acts as a routing identifier for Ad Sync to determine which SEEK account should receive the position.

A mapping of `seekSubOrgReference` values to SEEK accounts is configured during an organization’s onboarding with SEEK Ad Sync and must be consistent with the identifiers used in your software.

Provide stable identifiers for the most specific business unit or cost centre that an organization can set as the owner of a position.
When an organization onboards to Ad Sync,
they must be able to export these identifiers and work with SEEK to define a mapping to their SEEK hirer accounts.

This mapping is managed within SEEK to allow the hirer to modify their mapping or SEEK account structure without requiring any changes to your integration.

## Example

A hiring organization may have multiple offices, subsidiary businesses, or other business units that need separate SEEK accounts.
Their position routing may be configured as follows:

| Business unit    | `seekCompanyId`   | SEEK account            |
| :--------------- | :---------------- | :---------------------- |
| Sydney Office    | `acme-sydney`     | Acme Corp - Sydney      |
| Melbourne Office | `acme-melbourne`  | Acme Corp - Melbourne   |
| Perth Office     | `acme-perth`      | Acme Corp - Perth       |
| Acme Retail      | `acme-retail`     | Acme Retail Pty Ltd     |
| Acme Logistics   | `acme-logistics`  | Acme Logistics Pty Ltd  |
| Acme Technology  | `acme-technology` | Acme Technology Pty Ltd |

The `seekSubOrgReference` needs to be unique within the context of a specific `seekOrgReference`.

Then, when syncing a position for the Melbourne office:

```json
{
  "postingRequester": {
    "seekOrgReference": "abc-123",
    "seekOrgName": "Acme Corp",
    "seekSubOrgReference": "acme-melbourne",
    "seekAdSyncId": "seek-adSync-456"
  },
  "seekPartnerPositionId": "position-789"
}
```

Or when syncing a position for the logistics subsidiary:

```json
{
  "postingRequester": {
    "seekOrgReference": "abc-123",
    "seekOrgName": "Acme Corp",
    "seekSubOrgReference": "acme-logistics",
    "seekAdSyncId": "seek-adSync-456"
  },
  "seekPartnerPositionId": "position-790"
}
```

## Fallback behaviour

If the `seekSubOrgReference` is not provided or does not match any configured identifiers, Ad Sync can default to posting the position under the primary account associated with the `seekAdSyncId` if one is available.

{/* prettier-ignore */}
<Directional /> Ad Sync may be extended in a later release to allow your software to share positions without a `seekAdSyncId`, SEEK will default to posting the position under a new SEEK hirer account.
